import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';
import { Grid, Stack } from '@mui/material';
import WushPopover from './WushPopover';
import { AccountCircle, CalendarMonth, Category, ConfirmationNumber, House, Inventory, Logout, People, Settings, Storefront } from '@mui/icons-material';
import useAuthStore from '../../stores/useAuthStore';

const drawerWidth = 240;

const mainItems = [
  {
    text: 'Home',
    icon: <HomeIcon />,
    url: '/'
  },
  {
    text: 'Calendar',
    icon: <CalendarMonth />,
    url: '/calendar'
  }
];

const inventoryItems = [
  {
    text: 'Categories',
    icon: <Category />,
    url: '/item_categories'
  },
  {
    text: 'Items',
    icon: <Inventory />,
    url: '/inventory'
  },
  {
    text: 'Locations',
    icon: <House />,
    url: '/locations'
  }
];

const salesItems = [
  {
    text: 'Orders',
    icon: <ConfirmationNumber />,
    url: '/orders'
  },
  {
    text: 'Customers',
    icon: <People />,
    url: '/customers'
  },
  {
    text: 'Store Pages',
    icon: <Storefront />,
    url: '/store_pages'
  }
];

const configurationItems = [
  {
    text: 'Configuration',
    icon: <Settings />,
    url: '/configuration'
  }
];

const sidebar = [
  {
    title: "Main",
    items: mainItems
  },
  {
    title: "Inventory",
    items: inventoryItems
  },
  {
    title: "Sales",
    items: salesItems
  },
  {
    title: "Settings",
    items: configurationItems
  }
];

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export default function Sidebar({children}) {
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);

  const navigate = useNavigate();
  const { organization } = useAuthStore();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}  className="hide-print">
        <Toolbar>
          <Grid container>
            <Grid item xs={6} display="flex">
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                sx={{ mr: 2, ...(open && { display: 'none' }) }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h5" noWrap component="div" sx={{ my: 'auto' }} fontWeight={700}>
                {organization.name}
              </Typography>
            </Grid>
            <Grid item xs={6} display="flex" justifyContent="right">
              <WushPopover
                anchor={
                  <IconButton>
                    <AccountCircle sx={{ width: '24px', height: '24px' }} />
                  </IconButton>
                }
              >
                <Stack p={2}>
                  <List>
                    <ListItem
                      disablePadding
                    >
                      <ListItemButton
                        onClick={() => navigate('/logout')}
                      >
                        <ListItemIcon>
                          <Logout />
                        </ListItemIcon>
                        <ListItemText primary={"Logout"} />
                      </ListItemButton>
                    </ListItem>
                  </List>
                </Stack>
              </WushPopover>
            </Grid>
          </Grid>

        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
        className="hide-print"
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        {sidebar.map((section) => (
          <React.Fragment key={section.title}>
            <List key={section.title}>
              <ListItem>
                <Typography variant="h6" noWrap component="div" sx={{ my: 'auto' }}>
                  {section.title}
                </Typography>
              </ListItem>
              {section.items.map((item) => (
                <ListItem
                  key={item.text}
                  disablePadding
                  sx={{
                    bgcolor: item.url === "/" + window.location.pathname.split("/")[1] && theme.palette.active.main
                  }}
                >
                  <ListItemButton
                    onClick={() => navigate(item.url)}
                    sx={{
                      py: 0.5
                    }}
                  >
                    <ListItemIcon>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            <Divider />
          </React.Fragment>
        ))}
      </Drawer>
      <Main open={open}>
        <DrawerHeader />
        {children}
      </Main>
    </Box>
  );
}